import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import ClearAll from "./ClearAll";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import { TOOL_PANELS } from "../../components/EquipmentList/Constants/constants";
import {
  OwcIconButton,
  OwcTooltip,
  OwcTypography,
  OwcIcon,
  OwcSelectDropdown,
  OwcListItem,
  OwcEditableDropdown
} from "@one/react";
import { LIST_VIEW, SHOW_ADVANCE_FILTER_TOOLTIP, MY_LAB, ALL_EQUIPMENT } from "../../constants";
import Search from "./Search";
import moment from "moment-timezone";
import BookingViewChanger from "../booking-calendars/booking-view-changer/BookingViewChanger";
import { withApollo } from "react-apollo";
import { LIST_DIGITAL_LAB_BOOKING_USERS } from "../../gql/bookingapi";
import { getAllData } from "../../utils/helpers/fetching";
import { getDefaultDateRange } from "../../utils/helpers/text";
import { IconButton } from "@mui/material";
import ColumnsOutlinedGrey from "../../components/EquipmentList/icons/ColumnsOutlinedGrey";
import ColumnsOutlinedActive from "../../components/EquipmentList/icons/ColumnsOutlinedActive";
import FilterOutlinedGrey from "../../components/EquipmentList/icons/FilterOutlinedGrey";
import FilterOutlinedActive from "../../components/EquipmentList/icons/FilterOutlinedActive";
import GoogleCalendarSync from "../../components/GoogleCalendarSync";
import GoogleCalendarErrorBoundary from "../../components/GoogleCalendarErrorBoundary";
import SigneeFilter from "./SigneeFilter";
import { handleOnCellClick } from "../booking-calendars/helpers";
import styles from "./BookingFilter.module.scss";
import { useSelector } from "react-redux";
import {BOOKING_STORAGE_KEYS, getPersistedFilter} from "../../utils/store/bookingFilterPersistence";
import TvModeToggle from "./TvModeToggle";

const BookingFilterStyled = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  padding-left: 10px;
  flex-wrap: wrap;
  gap: 10px
`;

const BookingFilter = ({
 client,
 setResetAll,
 showAdvanceFilter,
 setShowAdvanceFilter,
 selectedDateRange,
 setSelectedDateRange,
 isTimelineView,
 setDisableResetAll,
 setEnableResetAll,
 enableResetAll,
 searchQuery,
 setSearchQuery,
 setTempRowData,
 view,
 setView,
 isEquipmentView,
 setIsEquipmentView,
 listGroupByView,
 setListGroupByView,
 savedFilter,
 setSavedFilter,
 signeeFilter,
 setSigneeFilter,
 displayPreset,
 setDisplayPreset,
 groupName,
 isGroupView = false,
 showInvalidFilter = false,
 userFilters,
 userDisplayColumnMaster,
 myLabFilter, setCurrentSideBarPanel, setShowSideBar, showSideBar,
 isFilterActive, setIsFilterActive, isColumnActive, setIsColumnActive,
 showGoogleCalendarSync = true,  // New prop to control Google Calendar sync visibility
}) => {
  momentTimezone.moment = moment;
  const user = useSelector((state) => state.user);
  const isTvMode = useSelector((state) => state?.bookingInstruments?.isTvMode);

  const { fetching, dispatchAction } = useContext(PaginationContext);
  const dataTimeZone = "utc";
  const displayTimeZone = user.timezone;
  const [allUsers, setAllUsers] = useState([]);

  const displayPresetOptions = useMemo(() => {
    return (userDisplayColumnMaster || [])
      .map((p) => ({ name: p.displayName + p.userID, label: p.displayName, definition: p.displayDefinition }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [userDisplayColumnMaster]);

  const setDefaultTimeRange = useCallback(() => {
    setSelectedDateRange(getDefaultDateRange());
  }, [setSelectedDateRange]);

  const handleDisplayPresetChange = useCallback((selectedSavedColumn) => {
    setDisplayPreset(selectedSavedColumn); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isGroupView && !showAdvanceFilter) {
      setShowAdvanceFilter(true);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupView]);

  const savedFilterOptions = useMemo(() => {
    const myLab = [];
    const allEquipment = [];
    const rest = [];

    if (myLabFilter) {
      myLab.push({ name: MY_LAB, label: "My Lab" });
    }

    userFilters.forEach(filter => {
      if (filter.filterName && filter.filterName !== MY_LAB) {
        rest.push({ name: filter.filterName, label: filter.filterName });
      }
    });

    if (signeeFilter && signeeFilter !== "All users") {
      allEquipment.push({ name: ALL_EQUIPMENT, label: "All Equipment" });
    }

    rest.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );

    return [...myLab, ...allEquipment, ...rest];
  }, [signeeFilter, myLabFilter, userFilters]);

  useEffect(() => {
    const allEquipmentFilterCondition = savedFilter === ALL_EQUIPMENT && !savedFilterOptions.some(f => f.name === ALL_EQUIPMENT);
    const myLabFilterCondition = savedFilter === MY_LAB && !savedFilterOptions.some(f => f.name === MY_LAB);
    if (savedFilterOptions.length && (!savedFilter || (allEquipmentFilterCondition || myLabFilterCondition))) {
      const persistedFilter = getPersistedFilter(BOOKING_STORAGE_KEYS.SAVED_FILTER);
      setSavedFilter(isGroupView ? (persistedFilter || savedFilterOptions[0].name) : savedFilterOptions[0].name)
    }
  }, [savedFilterOptions, savedFilter, setSavedFilter, isGroupView]);


  const loadUsers = useCallback(async () => {
    try {
      const { items } = await getAllData({
        client,
        query: LIST_DIGITAL_LAB_BOOKING_USERS,
        dataPath: ["data", "listDigitalLabBookingUsers"],
        variables: { limit: 1000 },
        cache: true,
      });

      setAllUsers(items || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, [client]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!signeeFilter && allUsers.length > 0) {
      // Default to "All users" (empty string)
      setSigneeFilter("");
    }

    if (!savedFilter && savedFilterOptions.length > 0) {
      const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
      if (myLabOption && !isTvMode) {
        setSavedFilter(MY_LAB);
      } else {
        setSavedFilter(savedFilterOptions[0].name);
      }
    }
  }, [myLabFilter, userFilters, allUsers, signeeFilter, savedFilter, savedFilterOptions, setSavedFilter, setSigneeFilter, isTvMode]);

  const handleSavedFilterChange = useCallback((selectedFilterName) => {
    if (selectedFilterName === "" && (!signeeFilter || signeeFilter === "" || signeeFilter === "All users")) {
      return;
    }
    if (!isGroupView) {
      setSavedFilter(selectedFilterName);
    }

    if (selectedFilterName === "" && selectedFilterName !== ALL_EQUIPMENT) {
      if (signeeFilter === "") {
        // Set to current user's bookings
        const userData = allUsers.find(u => u.userId === user.user);
        if (userData) {
          setSigneeFilter(userData);
        }
      }
    }

    dispatchAction({ type: "clearAll" });
    setTempRowData([]);
    setDisableResetAll(false);
  }, [signeeFilter, user.user, allUsers, setSavedFilter, setSigneeFilter, dispatchAction, setTempRowData, setDisableResetAll, isGroupView]);

  const handleSigneeFilterChange = useCallback((selectedSignee) => {
    const userData =
      selectedSignee.name === "" ? ""
        : selectedSignee.data
        || allUsers.find(u => u.userId === selectedSignee.name)
        || selectedSignee;
    setSigneeFilter(userData);

    if (selectedSignee.name === "" && savedFilter === "" && savedFilter !== ALL_EQUIPMENT) {
      setSavedFilter(MY_LAB);
    }

    dispatchAction({ type: "clearAll" });
    setTempRowData([]);
    setDisableResetAll(false);
  }, [allUsers, savedFilter, setSigneeFilter, setSavedFilter, dispatchAction, setTempRowData, setDisableResetAll]);

  const handleReset = useCallback(() => {
    let defaultSavedFilter = "";
    if (isGroupView) {
      const pathSegments = window.location.pathname.split("/");
      const groupFilterName = pathSegments[pathSegments.length - 1];
      const groupFilterOption = savedFilterOptions.find(
        option => option.name === groupFilterName
      );
      defaultSavedFilter = groupFilterOption?.name || savedFilterOptions[0].name;
    } else {
      const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
      if (myLabOption) {
        defaultSavedFilter = MY_LAB;
      } else {
        defaultSavedFilter = savedFilterOptions[0].name;
      }
    }
    setSavedFilter(defaultSavedFilter);

    // Reset to "All users" (empty string)
    setSigneeFilter("");

    setTimeout(() => setDefaultTimeRange())
    setSearchQuery("");
    setResetAll(prev => !prev);
    setEnableResetAll(false)
    setTempRowData([]);
    setDisplayPreset(null)
    setDisableResetAll(true);

    dispatchAction({ type: "clearAll" }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterOptions, listGroupByView, enableResetAll, isGroupView, allUsers, setSavedFilter, setSigneeFilter, setSearchQuery, setResetAll, setTempRowData, setDisableResetAll, setDisplayPreset, dispatchAction]);

  // Add effect to handle view switching
  useEffect(() => {
    // Clear temporary data when switching views to prevent stale data

    setTempRowData([]);
    dispatchAction({ type: "clearAll" });
  }, [view, isEquipmentView, setTempRowData, dispatchAction]);

   const tvDateLabel = useMemo(() => {
     try {
       const tz = user?.timezone || 'UTC';
       return new Intl.DateTimeFormat('en-US', {
           timeZone: tz,
           month: 'long',
           day: 'numeric',
           year: 'numeric',
         }).format(new Date());
     } catch {
       return new Date().toLocaleDateString('en-US', {
           month: 'long', day: 'numeric', year: 'numeric'
       });
     }
   }, [user?.timezone]);


  // Add effect to handle groupName changes in group view
  useEffect(() => {
    if (isGroupView && groupName && groupName !== savedFilter && savedFilterOptions.find((option) => option.name === groupName)) {
      setSavedFilter(groupName);
      // Clear temporary data when group changes
      setTempRowData([]);
      dispatchAction({ type: "clearAll" });
      setDisableResetAll(false);
    }
  }, [groupName, isGroupView, savedFilter, setSavedFilter, setTempRowData, dispatchAction, setDisableResetAll, savedFilterOptions]);

  const isResetDisabled = useMemo(() => {
    if (savedFilterOptions.length === 0) {
      return true;
    }
    if (enableResetAll) {
      return false
    }

    let defaultSavedFilter = "";
    const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
    if (myLabOption) {
      defaultSavedFilter = MY_LAB;
    } else {
      defaultSavedFilter = savedFilterOptions[0].name;
    }

    const defaultSigneeIsEmpty = signeeFilter === "" ||
      (typeof signeeFilter === "object" && (!signeeFilter.userId || signeeFilter.userId === ""));

    const [defaultRangeStart, defaultRangeEnd] = getDefaultDateRange();
    const dateChanged =
      !selectedDateRange ||
      selectedDateRange.length !== 2 ||
      !moment(selectedDateRange[0]).isSame(defaultRangeStart, "day") ||
      !moment(selectedDateRange[1]).isSame(defaultRangeEnd, "day");

    const isAtDefaults =
      (savedFilter === defaultSavedFilter || isGroupView) &&
      defaultSigneeIsEmpty &&
      searchQuery === "" &&
      displayPreset === null &&
      !dateChanged;

    return isAtDefaults;
  }, [savedFilterOptions, enableResetAll, signeeFilter, savedFilter, searchQuery, displayPreset, isGroupView, selectedDateRange]);

  if (showInvalidFilter) {
    return <></>
  }

  return (
    <>
      <div className={`${styles.pageTitle} ${isTvMode ? styles.pageTitleTv : ''}`}>
        {isTvMode ? (
           <OwcTypography
             style={{
               fontFamily: 'Roche Sans',
               fontWeight: 500,
               fontStyle: 'normal',
               fontSize: 16,
               color: '#0B41CD'
             }}
           >
             {tvDateLabel}
           </OwcTypography>
         ) : (
           <OwcTypography variant="button">
             {`${isGroupView ? `Group views - ${savedFilter}` : 'All bookings'}`}
           </OwcTypography>
       )}

        <TvModeToggle visible={isTimelineView} />
      </div>

      {(view !== LIST_VIEW && !isTvMode) && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px"
        }}>
          <BookingFilterStyled>
            <>
              <OwcTooltip anchor={"advance-filter"} placement="top">
                {SHOW_ADVANCE_FILTER_TOOLTIP}
              </OwcTooltip>
              <OwcIconButton
                id="advance-filter"
                data-testid="advance-filter-button"
                style={{ marginRight: 8 }}
                icon=" parameter_set"
                type="filled"
                onClick={() => {
                  setShowAdvanceFilter((prev) => !prev);
                }}
                flat />
            </>

            {showAdvanceFilter && (
              <>
                {!(listGroupByView === 'BOOKING' && view === LIST_VIEW) && (<div>
                  <OwcSelectDropdown
                    style={{ width: 200 }}
                    label="Select predefined filter"
                    dataTestid="equipment-filter-dropdown"
                    name="savedFilter"
                    disabled={isGroupView}
                    onSelectChange={(ev) => {
                      const selectedOption = savedFilterOptions[ev.detail[0]];
                      if (selectedOption) {
                        handleSavedFilterChange(selectedOption.name);
                      }
                    }}
                  >
                    {savedFilterOptions.map((option, index) => (
                      <OwcListItem
                        key={option.name}
                        value={option.name}
                        selected={savedFilter === option.name}
                        data-testid={"equipment-filter-menu-item-" + (option.name || "all")}
                      >
                        {option.label}
                      </OwcListItem>
                    ))}
                  </OwcSelectDropdown>

                </div>)}

                <SigneeFilter
                  signeeFilter={signeeFilter}
                  allUsers={allUsers}
                  onSigneeFilterChange={handleSigneeFilterChange}
                  width={200}
                />
                {!isGroupView && <div style={{ margin: 'auto 0', marginLeft: 8 }}>
                  <ClearAll
                    onClickCallback={handleReset}
                    disabled={isResetDisabled} />
                </div>}
              </>
            )}
          </BookingFilterStyled>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* Google Calendar Sync - only show in equipment view */}
            {showGoogleCalendarSync && isEquipmentView && (
              <GoogleCalendarErrorBoundary key={`calendar-error-boundary-${isEquipmentView}`}>
                <GoogleCalendarSync key={`calendar-sync-${isEquipmentView}-${Date.now()}`} />
              </GoogleCalendarErrorBoundary>
            )}

            <BookingViewChanger
              view={view}
              setView={setView}
              setTempRowData={setTempRowData}
              isEquipmentView={isEquipmentView}
              setIsEquipmentView={setIsEquipmentView}
              listGroupByView={listGroupByView}
              setListGroupByView={setListGroupByView}
              isToggle={true} />

            <div className={styles.lowResTvModeToggle}>
              <TvModeToggle visible={isTimelineView} />
            </div>

          </div>
        </div>
      )}

      {!isTvMode && (<div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: "10px"
        }}
      >
        {view === LIST_VIEW && (
          <>
            <BookingFilterStyled data-testid="booking-filter-admin" style={{paddingLeft:'16px'}}>
              <Search
                onChange={(e) => setSearchQuery(e.detail)}
                onClear={() => setSearchQuery("")}
                label="Search all columns"
                placeholder="Search all columns"
                testid={"common-search-drop-booking"}
                fetching={fetching}
                compact={false}
                searchQuery={searchQuery}
              ></Search>

              <div style={{ padding: "0 5px 0 5px" }} className="date-picker-my-booking">
                <Datepicker
                  id="datepicker-admin-panel"
                  controls={["calendar"]}
                  value={selectedDateRange}
                  inputStyle="box"
                  style={{ height: "5px" }}
                  select="range"
                  display="anchored"
                  inRangeInvalid={true}
                  rangeEndInvalid={true}
                  startIcon={<OwcIcon name="date" type="outlined" />}
                  onTempChange={handleOnCellClick}
                  headerText="Experiment date(range)"
                  label={"Date range"}
                  touchUi={"auto"}
                  dateFormat="D-MMM-YYYY"
                  onClose={(param) => {
                    if (param?.value) {
                      dispatchAction({
                        type: "clearAll",
                      });
                      setTempRowData([]);
                      setDisableResetAll(false);
                      setSelectedDateRange(param?.value);
                    }
                  }}
                  timezonePlugin={momentTimezone}
                  dataTimezone={dataTimeZone}
                  displayTimezone={displayTimeZone}
                  buttons={["cancel", "set"]}
                  compact
                  exclusiveEndDates={false}
                />
              </div>

              <>
                <OwcTooltip anchor={"advance-filter"} placement="top">
                  {SHOW_ADVANCE_FILTER_TOOLTIP}
                </OwcTooltip>
                <OwcIconButton
                  id="advance-filter"
                  data-testid="advance-filter-button"
                  style={{ marginRight: 8 }}
                  icon=" parameter_set"
                  type="filled"
                  onClick={() => {
                    setShowAdvanceFilter((prev) => !prev);
                  }}
                  flat
                />
              </>

              {showAdvanceFilter && (
                <>
                  <div style={{ display: 'flex' }}>
                    {!(listGroupByView === 'BOOKING' && view === LIST_VIEW) && (<OwcSelectDropdown
                      style={{ width: 200 }}
                      label="Select predefined filter"
                      dataTestid="equipment-filter-dropdown"
                      name="savedFilter"
                      disabled={listGroupByView === 'BOOKING' && view === LIST_VIEW}
                      onSelectChange={(ev) => {
                        const selectedOption = savedFilterOptions[ev.detail[0]];
                        if (selectedOption) {
                          handleSavedFilterChange(selectedOption.name);
                        }
                      }}
                    >
                      {savedFilterOptions.map((option, index) => (
                        <OwcListItem
                          key={option.name}
                          value={option.name}
                          selected={savedFilter === option.name}
                          data-testid={"equipment-filter-menu-item-" + (option.name || "all")}
                        >
                          {option.label}
                        </OwcListItem>
                      ))}
                    </OwcSelectDropdown>)}

                    {listGroupByView !== 'BOOKING' && view === LIST_VIEW && (<IconButton
                      id="filter-list"
                      onClick={() => {
                        setIsColumnActive(() => false);
                        if (showSideBar) {
                          setShowSideBar(false);
                          setCurrentSideBarPanel(TOOL_PANELS.filters);
                          if (!isFilterActive) {
                            setTimeout(() => {
                              setShowSideBar(true);
                            });
                          }
                          setIsFilterActive((prevState) => !prevState);
                        } else {
                          setCurrentSideBarPanel(TOOL_PANELS.filters);
                          setIsFilterActive(() => true);
                          setShowSideBar(() => true);
                        }
                      }}
                    >
                      {!isFilterActive && (
                        <>
                          <FilterOutlinedGrey />
                        </>
                      )}
                      {isFilterActive && (
                        <>
                          <FilterOutlinedActive />
                        </>
                      )}
                    </IconButton>)}
                  </div>


                  {!(listGroupByView === 'BOOKING' && view === LIST_VIEW) && (<div style={{ display: 'flex' }}>
                    <OwcEditableDropdown
                      key={displayPreset?.name}
                      style={{ width: 200 }}
                      label="Predefined columns"
                      disabled={listGroupByView === 'BOOKING' && view === LIST_VIEW}
                      dataTestid="Predefined columns"
                      name="Predefined columns"
                      onSelectChange={(ev) => {
                        const selected = displayPresetOptions[ev.detail];
                        if (selected) {
                          handleDisplayPresetChange(selected);
                        } else handleDisplayPresetChange(null);
                      }}>
                      {displayPresetOptions.map((option, idx) => (
                        <OwcListItem key={option.name} value={option.name} selected={displayPreset?.name === option.name}>
                          {option.label}
                        </OwcListItem>
                      ))}
                    </OwcEditableDropdown>
                    {listGroupByView !== 'BOOKING' && view === LIST_VIEW && (<IconButton
                      id="column-filter"
                      onClick={() => {
                        setIsFilterActive(() => false);
                        if (showSideBar) {
                          setShowSideBar(false);
                          setCurrentSideBarPanel(TOOL_PANELS.columns);
                          if (!isColumnActive) {
                            setTimeout(() => {
                              setShowSideBar(true);
                            });
                          }
                          setIsColumnActive((prevState) => !prevState);
                        } else {
                          setCurrentSideBarPanel(TOOL_PANELS.columns);
                          setIsColumnActive(() => true);
                          setShowSideBar(() => true);
                        }
                      }}
                    >
                      {!isColumnActive && (
                        <>
                          <ColumnsOutlinedGrey />
                        </>
                      )}
                      {isColumnActive && (
                        <>
                          <ColumnsOutlinedActive />
                        </>
                      )}
                    </IconButton>)}
                  </div>
                  )}

                  <SigneeFilter
                    signeeFilter={signeeFilter}
                    allUsers={allUsers}
                    onSigneeFilterChange={handleSigneeFilterChange}
                    width={200}
                  />
                  {!isGroupView && <div style={{ margin: 'auto 0', marginLeft: 16 }}>
                    <ClearAll
                      onClickCallback={handleReset}
                      disabled={isResetDisabled}
                    />
                  </div>}
                </>
              )}
            </BookingFilterStyled>

            <BookingViewChanger setTempRowData={setTempRowData} view={view} setView={setView} setListGroupByView={setListGroupByView} listGroupByView={listGroupByView} isEquipmentView={isEquipmentView} setIsEquipmentView={setIsEquipmentView} />
          </>
        )}
      </div>)}
    </>
  );
};

export default withApollo(BookingFilter);
